import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import {
  Component,
  Injector,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  afterNextRender,
  inject,
  signal
} from '@angular/core';
import _ from 'lodash';
import {
  ActivatedRoute,
  NavigationStart,
  PRIMARY_OUTLET,
  Router
} from '@angular/router';
import {
  Feature as FeatureAccess,
  FeatureBase
} from './../kuba/features/models/features-base.model';
import { FeaturesBaseServices } from './../kuba/features/services/features-base.services';
import { BaseServices } from './../kuba/kuba.services';
import { Rights } from '../_models/feature';
import { FeatureKey } from '../_models/feature';
import { BusinessServices } from '../kuba/businesses/services/business.services';
import { BusinessPortalService } from './../kuba/portals/services/business-portal.services';
import { Subscription } from 'rxjs';
import { BookingPortalService } from '../kuba/flexi/booking-portal/booking-portal.service';
import { isNavigableFlexiTab } from '../shared/ecpl-tab-navigation/tab-navigation.rules';
import { brandedTranslationKey } from '../shared/common/branding.util';
import { NavigationStateService } from '../shared/services/navigation-state.service';
import {
  SubNavigation,
  SubNavigationContext,
  buildSubNavigation,
  keepRoutable,
  matchesRoutePattern,
  routePatterns
} from './sub-navigation/sub-navigation';

/** Grace period for moving the pointer between a module and its panel. */
const SUB_NAVIGATION_CLOSE_DELAY_MS = 150;

interface OpenSubNavigation {
  feature: any;
  menu: SubNavigation;
  /** The navigation item the panel is attached to. */
  origin: HTMLElement;
}

@Component({
  selector: 'navigation',
  templateUrl: 'navigation.component.html',
  styleUrls: ['navigation.component.scss'],
  standalone: false
})
export class NavigationComponent implements OnInit, OnChanges {
  features: any = [];
  navSource: any = [];
  businessId: number = BaseServices.BusinessId;
  currentUserRole: string;
  appId: number;
  featureBase: FeatureBase;
  featuresAccess: FeatureAccess[];
  placeholder = true;
  @Input() urlBase: string;
  private subscriptions: Subscription[] = [];
  /** The module whose hover sub-navigation is open. */
  readonly subNavigation = signal<OpenSubNavigation | null>(null);
  /** Beside the item; bottom-aligned with it when there is no room below. */
  readonly subNavigationPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: -8
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: 8
    }
  ];
  @ViewChild(CdkConnectedOverlay)
  private subNavigationOverlay?: CdkConnectedOverlay;
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  /** Keyed by feature object, so a reload that replaces the features recomputes. */
  private subNavigationCache = new WeakMap<object, SubNavigation | null>();
  private readonly canHover =
    typeof window.matchMedia !== 'function' ||
    window.matchMedia('(hover: hover)').matches;
  private subNavigationCloseTimer?: ReturnType<typeof setTimeout>;
  private restoringFocus = false;
  private routeShapes?: string[][];
  private bookingAccessRequest?: Subscription;
  private bookingAccessBusinessId: number | null = null;
  private canOrderBht = false;

  constructor(
    public businessServices: BusinessServices,
    public featuresBaseServices: FeaturesBaseServices,
    public businessPortalService: BusinessPortalService,
    public route: ActivatedRoute,
    public navigation: NavigationStateService
  ) {}

  /**
   * Translation key to show for a menu item. Feature JSON still carries
   * Kuba-branded keys (ADMIN_NEWS, ABOUT_KUBA); white-label platforms such as
   * Salutis map those onto their own keys - see shared/common/branding.util.
   */
  navLabelKey(feature: any): string {
    return brandedTranslationKey(feature?.translationKey, this.appId);
  }

  reload() {
    // Logic to refresh/reload data
    this.loadData(); 
  }
  ngOnInit() {
    this.loadData();
    this.subscriptions.push(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          this.closeSubNavigation();
        }
      })
    );
  }
  
  ngOnChanges(changes: SimpleChanges) {
    if (changes['urlBase'] && !changes['urlBase'].firstChange) {
      this.resetBookingAccess();
      this.loadBookingAccess();
    }
  }

  private resetBookingAccess() {
    this.bookingAccessRequest?.unsubscribe();
    this.bookingAccessBusinessId = null;
    this.canOrderBht = false;
    this.subNavigationCache = new WeakMap<object, SubNavigation | null>();
    this.closeSubNavigation();
  }

  loadData() {
    this.resetBookingAccess();
    this.currentUserRole = BaseServices.UserRole;
    this.appId = BaseServices.ApplicationId;
    if (BaseServices.roleId === '1') {
      this.features = BaseServices.userFeatures;
      if (this.currentUserRole !== 'Editor') {
        _.remove(this.features, (n: any) => {
          return n.translationKey === 'USER_LIST';
        });
        _.remove(this.features, (n: any) => {
          return n.translationKey === 'OUR_COMPANY';
        });
      }
      if (this.currentUserRole === 'Editor') {
        let index = _.findIndex(this.features, [
          'translationKey',
          'OUR_COMPANY'
        ]);
        if (index >= 0) {
          this.features[index].linkText = _.replace(
            this.features[index].linkText,
            '%s',
            this.businessId.toString()
          );
        }
      }
      this.placeholder = false;
    } else if (BaseServices.roleId === '2') {
      this.subscriptions.push(
        this.businessPortalService
          .getPortalFeatures(BaseServices.PortalId)
          .subscribe((res: any) => {
            if (res) {
              let featureBase = BaseServices.getUserFeatures();
              let template = <any>JSON.parse(featureBase.FeaturesTemplate!);
              featureBase.FeaturesTemplate = JSON.stringify(
                template.filter(
                  (x: any) =>
                    (x.navigationRoles.indexOf('P') > -1 &&
                      x.IsFeature.indexOf('N') > -1) ||
                    (x.navigationRoles.indexOf('P') > -1 && x.IsDefault === 1)
                )
              );
              featureBase.Features = res.Features;
              featureBase.Details = res;
              let baseFeatures =
                FeaturesBaseServices.getFeatureDetails(featureBase);
              this.features = baseFeatures.filter((x: any) => {
                if (res.Features === null) {
                  return x.IsDefault === 1;
                } else {
                  return x.checked === true;
                }
              });
              this.placeholder = false;
              let assignBusiness = {
                IsDefault: 0,
                IsFeature: 'FN',
                access: true,
                checked: true,
                id: 2000,
                key: '037',
                linkText: 'portal/edit/' + BaseServices.PortalId + '/user',
                name: 'assign-business',
                navigationRoles: 'P',
                translationKey: 'Assign Business'
              };
              if (BaseServices.roleId === '2') {
                //KW-666 - To reflect the change of Business List name for Portal users as well
                this.features.forEach((element: any) => {
                  if (element.id === 2) {
                    element.translationKey =
                      BaseServices.PortalBusinessListName !== ''
                        ? BaseServices.PortalBusinessListName
                        : element.translationKey;
                  }
                });
              }
              this.placeholder = false;
            }
            if (this.currentUserRole !== 'Editor') {
              _.remove(this.features, (n: any) => {
                return n.translationKey === 'USER_LIST';
              });
              _.remove(this.features, (n: any) => {
                return n.translationKey === 'OUR_COMPANY';
              });
            }
            if (this.currentUserRole === 'Editor') {
              let index = _.findIndex(this.features, [
                'translationKey',
                'OUR_COMPANY'
              ]);
              if (index >= 0) {
                this.features[index].linkText = _.replace(
                  this.features[index].linkText,
                  '%s',
                  this.businessId.toString()
                );
              }
            }
            BaseServices.setSessionData(
              'leftNav',
              JSON.stringify(this.features)
            );
          })
      );
    } else {
      this.subscriptions.push(
        this.businessServices
          .getById(BaseServices.BusinessId)
          .subscribe((result: any) => {
            if (result) {
              localStorage.setItem('BusinessDetails', JSON.stringify(result));
              let featureBase = BaseServices.getUserFeatures();
              let template = <any>JSON.parse(featureBase.FeaturesTemplate!);
              featureBase.FeaturesTemplate = JSON.stringify(
                template.filter(
                  (x: any) =>
                    (x.navigationRoles.indexOf('B') > -1 &&
                      x.IsFeature.indexOf('N') > -1) ||
                    (x.navigationRoles.indexOf('B') > -1 && x.IsDefault === 1)
                )
              );
              featureBase.Features = result.Features;
              featureBase.Type = 'business';
              featureBase.Details = result;
              let baseFeatures =
                FeaturesBaseServices.getFeatureDetails(featureBase);
              this.features = baseFeatures.filter((x: any) => {
                if (result.Features === null) {
                  return x.IsDefault === 1;
                } else if (x.id === FeatureKey.MY_PAGE) {
                  return x.id === FeatureKey.MY_PAGE;
                } else {
                  return x.checked === true || x.IsDefault === 1;
                }
              });
              this.placeholder = false;
            }
            if (this.currentUserRole !== 'Editor') {
              _.remove(this.features, (n: any) => {
                return n.translationKey === 'USER_LIST';
              });
              _.remove(this.features, (n: any) => {
                return n.translationKey === 'OUR_COMPANY';
              });
            }
            if (this.currentUserRole === 'Editor') {
              let index = _.findIndex(this.features, [
                'translationKey',
                'OUR_COMPANY'
              ]);
              if (index >= 0) {
                this.features[index].linkText = _.replace(
                  this.features[index].linkText,
                  '%s',
                  this.businessId.toString()
                );
              }
              let mypageindex = _.findIndex(this.features, [
                'id',
                FeatureKey.MY_PAGE
              ]);
              if (index >= 0) {
                this.features[mypageindex].translationKey = 'MY_PAGE';
              }
            }
            if (this.currentUserRole === 'Guest') {
              if (
                BaseServices.FeatureId === FeatureKey.CONTACTS ||
                BaseServices.FeatureId === FeatureKey.EMPLOYEES
              ) {
                let userRightsId = Rights.CHOOSE_PROJECTFDV; // check rights for project/fdv tab
                let showProjectFdv = BaseServices.checkUserRights(
                  userRightsId,
                  this.currentUserRole
                );
                if (showProjectFdv === true) {
                  let filterFeaturesKey = [
                    FeatureKey.DASHBOARD,
                    FeatureKey.EMPLOYEES,
                    FeatureKey.FOLLOW_UP,
                    FeatureKey.CONTACTS,
                    FeatureKey.MANUALS
                  ];
                  this.subscriptions.push(
                    this.businessServices
                      .LeftNavigationAssignAccess(
                        this.businessId,
                        this.appId,
                        this.currentUserRole
                      )
                      .subscribe((res: any) => {
                        if (res.projectCount > 0) {
                          filterFeaturesKey.push(FeatureKey.PROJECT);
                        }
                        if (res.fdvCount > 0) {
                          filterFeaturesKey.push(FeatureKey.FDV);
                        }
                        if (res.vehicleCount > 0) {
                          filterFeaturesKey.push(FeatureKey.VEHICLES);
                        }
                        if (res.frameworkAgreementCount > 0) {
                          filterFeaturesKey.push(
                            FeatureKey.FRAMEWORK_AGREEMENT
                          );
                        }
                        if (res.internalControlCount > 0) {
                          filterFeaturesKey.push(FeatureKey.INTERNAL_CONTROL);
                        }

                        if (res.kundeELCount > 0) {
                          filterFeaturesKey.push(FeatureKey.KUNDE_EL);
                        }
                        if (res.serviceAgreementCount > 0) {
                          filterFeaturesKey.push(FeatureKey.SERVICE_AGREEMENT);
                        }

                        this.features = _.filter(this.features, (x: any) => {
                          return (
                            filterFeaturesKey.indexOf(x.id) > -1 ||
                            (x.navigationRoles.indexOf('B') > -1 &&
                              x.IsDefault === 1)
                          );
                        });
                      })
                  );
                } else {
                  let filterFeaturesKey = [
                    FeatureKey.DASHBOARD,
                    FeatureKey.EMPLOYEES,
                    FeatureKey.FOLLOW_UP,
                    FeatureKey.CONTACTS,
                    FeatureKey.MANUALS
                  ];
                  this.subscriptions.push(
                    this.businessServices
                      .LeftNavigationAssignAccess(
                        this.businessId,
                        this.appId,
                        this.currentUserRole
                      )
                      .subscribe((res: any) => {
                        if (res.projectCount > 0) {
                          filterFeaturesKey.push(FeatureKey.PROJECT);
                        }
                        if (res.fdvCount > 0) {
                          filterFeaturesKey.push(FeatureKey.FDV);
                        }
                        if (res.foodSafetyCount > 0) {
                          filterFeaturesKey.push(FeatureKey.FOOD_SAFETY);
                        }
                        if (res.controlCount > 0) {
                          filterFeaturesKey.push(FeatureKey.KUBA_CONTROL);
                        }
                        if (res.frameworkAgreementCount > 0) {
                          filterFeaturesKey.push(
                            FeatureKey.FRAMEWORK_AGREEMENT
                          );
                        }
                        if (res.internalControlCount > 0) {
                          filterFeaturesKey.push(FeatureKey.INTERNAL_CONTROL);
                        }
                        if (res.vehicleCount > 0) {
                          filterFeaturesKey.push(FeatureKey.VEHICLES);
                        }
                        if (res.kundeELCount > 0) {
                          filterFeaturesKey.push(FeatureKey.KUNDE_EL);
                        }
                        if (res.serviceAgreementCount > 0) {
                          filterFeaturesKey.push(FeatureKey.SERVICE_AGREEMENT);
                        }
                        this.features = _.filter(this.features, (x: any) => {
                          return (
                            filterFeaturesKey.indexOf(x.id) > -1 ||
                            (x.navigationRoles.indexOf('B') > -1 &&
                              x.IsDefault === 1)
                          );
                        });
                      })
                  );
                }
              } else {
                if (+BaseServices.FeatureId === FeatureKey.CLIENT) {
                  let filterFeaturesKey = [FeatureKey.MY_PAGE];
                  this.features = _.filter(this.features, (x: any) => {
                    return (
                      filterFeaturesKey.indexOf(x.id) > -1 ||
                      (x.navigationRoles.indexOf('B') > -1 && x.IsDefault === 1)
                    );
                  });
                } else {
                  let filterFeaturesKey = [FeatureKey.MY_PAGE];
                  //KW-809- Project access removed for Subcontractor guest login

                  this.features = _.filter(this.features, (x: any) => {
                    return (
                      filterFeaturesKey.indexOf(x.id) > -1 ||
                      (x.navigationRoles.indexOf('B') > -1 && x.IsDefault === 1)
                    );
                  });
                }
                let dashboardIndex = _.findIndex(this.features, [
                  'translationKey',
                  'DASHBOARD'
                ]);
                if (dashboardIndex >= 0) {
                  this.features[dashboardIndex].linkText = 'my-page';
                }
                let filterFeatures = [
                  FeatureKey.EDIT_BUSINESS,
                  FeatureKey.DASHBOARD,
                  FeatureKey.CONTACTS
                ];
                _.remove(this.features, (n: any) => {
                  return filterFeatures.indexOf(n.id) > -1;
                });
                let index = _.findIndex(this.features, [
                  'id',
                  FeatureKey.MY_PAGE
                ]);
                if (index >= 0) {
                  this.features[index].translationKey = 'MY_PROFILE';
                }
                this.features.forEach(element => {
                  this.features = this.features.filter(x => x.name != 'Flexi'
                  );
                });
              }
            }
            let mypageindex = _.findIndex(this.features, [
              'id',
              FeatureKey.MY_PAGE
            ]);
            if (mypageindex >= 0) {
              this.features[mypageindex].translationKey = 'MY_PAGE';
            }
            if (BaseServices.roleId === '4') {
              this.features[mypageindex].translationKey = 'OUR_PAGE';
            }
            BaseServices.setSessionData(
              'leftNav',
              JSON.stringify(this.features)
            );
            if (window.name === 'Remote') {
              _.remove(this.features, (n: any) => {
                return n.translationKey === 'MY_PAGE';
              });
            } else {
              _.remove(this.features, (n: any) => {
                return n.checked === false && n.IsDefault === 0;
              });
            }
            if (BaseServices.roleId === '5') {
              let userRightsId = Rights.HIDE_EMPLOYEE_LIST;
              let showProjectFdv = BaseServices.checkUserRights(
                userRightsId,
                this.currentUserRole
              );
              if (showProjectFdv == true) {
                this.features.forEach((element: any) => {
                  this.features = this.features.filter(
                    (x: any) => x.name != 'Employees'
                  );
                });
              }
              let featureId = Rights.ADMINISTRATE_FLEXI;
              this.features.forEach(element => {
                this.features = this.features.filter(x => x.name != 'Flexi');
              });
            }
            this.loadBookingAccess();
          })
      );
    }
    this.loadBookingAccess();
    if (BaseServices.userFeatures != null) {
      var dashboard = BaseServices.userFeatures.filter(
        (x: any) => x.name == 'Dashboard'
      );
    }
    if (dashboard != null) this.setCurrentModule(dashboard);
  }
  checkFeatureStatus(feature: any): boolean {
    let featureAccess = false;
    let featuresAccess: any = this.featuresAccess;

    if (this.currentUserRole === 'Admin') {
      featureAccess = true;
    } else if (featuresAccess) {
      let checkFeature = new FeatureAccess();
      checkFeature = <FeatureAccess>_(featuresAccess)
        .thru(parent => {
          return _.union(parent, _.map(parent, 'children'));
        })
        .flatten()
        .find({ id: feature.id });

      if (checkFeature) {
        featureAccess =
          checkFeature.access && checkFeature.checked ? true : false;
      }
    }
    return featureAccess;
  }

  setFeatureBase() {
    this.featuresBaseServices.getFeatureAccess();
  }

  setFeatureValue(feature: any): boolean {
    let resultAccess = false;
    let featuresAccess: any = this.featuresAccess;
    if (!feature.IsFeature) {
      resultAccess = true;
    } else if (featuresAccess) {
      let checkFeature = new FeatureAccess();
      checkFeature = <FeatureAccess>_(featuresAccess)
        .thru(parent => {
          return _.union(parent, _.map(parent, 'children'));
        })
        .flatten()
        .find({ id: feature.id });

      if (checkFeature) {
        resultAccess =
          checkFeature.access && checkFeature.checked ? true : false;
        if (
          checkFeature.name === 'Manuals' &&
          BaseServices.UserRole === 'Guest'
        ) {
          let userRightsId = Rights.VIEW_MANUALS;
          checkFeature.access = BaseServices.checkUserRights(
            userRightsId,
            BaseServices.UserRole
          );
        }
      }
    }
    return resultAccess;
  }

  /**
   * Set working Module
   * @param feature {any}
   */
  setCurrentModule(feature: any) {
    sessionStorage.setItem('workingModule', JSON.stringify(feature));
  }

  /**
   * The hover sub-navigation for a navigation feature, or null when it has none.
   * Built from the features this navigation lists, which in R mode are the
   * entered business's.
   * @param feature Top-level feature from `features`.
   */
  subNavigationFor(feature: any): SubNavigation | null {
    if (!feature || typeof feature !== 'object') {
      return null;
    }
    if (!this.subNavigationCache.has(feature)) {
      this.subNavigationCache.set(
        feature,
        keepRoutable(
          buildSubNavigation(feature, this.subNavigationContext(), this.canOrderBht),
          commands => this.isRoutable(commands)
        )
      );
    }
    return this.subNavigationCache.get(feature) ?? null;
  }

  /** Whether commands, resolved like the module links, open a configured route. */
  private isRoutable(commands: any[]): boolean {
    this.routeShapes ??= routePatterns(this.router.config);
    const segments =
      this.router
        .createUrlTree(commands, { relativeTo: this.route })
        .root.children[PRIMARY_OUTLET]?.segments.map(segment => segment.path) ?? [];
    return matchesRoutePattern(segments, this.routeShapes);
  }

  onModuleMouseEnter(feature: any, event: MouseEvent) {
    if (this.canHover) {
      this.openSubNavigation(feature, event.currentTarget as HTMLElement);
    }
  }

  /** Keyboard focus opens the panel too; a mouse click's focus does not. */
  onModuleFocusIn(feature: any, event: FocusEvent) {
    const target = event.target as HTMLElement;
    if (!this.restoringFocus && target.matches(':focus-visible')) {
      this.openSubNavigation(feature, event.currentTarget as HTMLElement);
    }
  }

  onModuleFocusOut(event: FocusEvent) {
    if (!this.isInSubNavigationPanel(event.relatedTarget)) {
      this.scheduleSubNavigationClose();
    }
  }

  onSubNavigationFocusOut(event: FocusEvent) {
    const next = event.relatedTarget;
    if (
      !this.isInSubNavigationPanel(next) &&
      !(next instanceof Node && this.subNavigation()?.origin.contains(next))
    ) {
      this.scheduleSubNavigationClose();
    }
  }

  /** Arrow Right on a module moves into its open panel. */
  focusSubNavigation(event: Event) {
    const first = this.subNavigationOverlay?.overlayRef?.overlayElement.querySelector<HTMLElement>(
      'a[href], input'
    );
    if (this.subNavigation() && first) {
      event.preventDefault();
      first.focus();
    }
  }

  /**
   * A link in the panel was chosen: its module becomes the working module, as
   * with the module's own link. The feature is passed in because the router's
   * NavigationStart may already have closed the panel.
   * @param feature The module the panel belongs to.
   */
  onSubNavigationNavigated(feature: any) {
    this.setCurrentModule(feature);
    this.closeSubNavigation();
  }

  scheduleSubNavigationClose() {
    this.cancelSubNavigationClose();
    this.subNavigationCloseTimer = setTimeout(
      () => this.closeSubNavigation(),
      SUB_NAVIGATION_CLOSE_DELAY_MS
    );
  }

  cancelSubNavigationClose() {
    clearTimeout(this.subNavigationCloseTimer);
  }

  /**
   * Closes the panel.
   * @param restoreFocus Return keyboard focus to the module the panel belonged to.
   */
  closeSubNavigation(restoreFocus = false) {
    this.cancelSubNavigationClose();
    const open = this.subNavigation();
    if (!open) {
      return;
    }
    this.subNavigation.set(null);
    if (restoreFocus) {
      this.restoringFocus = true;
      open.origin.querySelector<HTMLElement>('a[href]')?.focus();
      this.restoringFocus = false;
    }
  }

  private openSubNavigation(feature: any, origin: HTMLElement) {
    this.cancelSubNavigationClose();
    const menu = this.navigation.mobile() ? null : this.subNavigationFor(feature);
    if (!menu) {
      this.closeSubNavigation();
      return;
    }
    if (this.subNavigation()?.feature === feature) {
      return;
    }
    this.subNavigation.set({ feature, menu, origin });
    // The panel's height changes with its content; place it again once rendered.
    afterNextRender(
      () => this.subNavigationOverlay?.overlayRef?.updatePosition(),
      { injector: this.injector }
    );
  }

  /** Check the same booking permission as the Flexi page, once per navigation context. */
  private loadBookingAccess() {
    const businessId = Number(/^\/?business\/(\d+)\/?$/.exec(this.urlBase)?.[1]);
    const flexi = this.features.find((feature: any) => feature.id === FeatureKey.FLEXI);
    if (!businessId || this.bookingAccessBusinessId === businessId ||
        !flexi || flexi.checked === false || flexi.access === false ||
        !flexi.children?.some((tab: any) =>
          tab.id === FeatureKey.FLEXI_BOOKING_PORTAL && isNavigableFlexiTab(tab))) {
      return;
    }
    this.bookingAccessBusinessId = businessId;
    this.bookingAccessRequest = this.injector.get(BookingPortalService)
      .canOrder(businessId).subscribe(canOrder => {
        this.canOrderBht = canOrder;
        this.subNavigationCache = new WeakMap<object, SubNavigation | null>();
        const open = this.subNavigation();
        if (open?.feature.id === FeatureKey.FLEXI) {
          const menu = this.subNavigationFor(open.feature);
          this.subNavigation.set(menu ? { ...open, menu } : null);
          afterNextRender(
            () => this.subNavigationOverlay?.overlayRef?.updatePosition(),
            { injector: this.injector }
          );
        }
      });
  }

  private isInSubNavigationPanel(target: EventTarget | null): boolean {
    const pane = this.subNavigationOverlay?.overlayRef?.overlayElement;
    return !!pane && target instanceof Node && pane.contains(target);
  }

  /** Reads the session lazily, so a rule only reads what it needs. */
  private subNavigationContext(): SubNavigationContext {
    return {
      urlBase: this.urlBase,
      get roleId() {
        return BaseServices.roleId;
      },
      get userRole() {
        return BaseServices.UserRole;
      },
      get isRemote() {
        return BaseServices.IsRmode;
      },
      hasRight: (rightId: number) =>
        BaseServices.checkUserRights(rightId, BaseServices.UserRole)
    };
  }

  ngOnDestroy() {
    this.bookingAccessRequest?.unsubscribe();
    this.cancelSubNavigationClose();
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}

export class FeatureDto {
  id: number;
  Title: string;
  LinkText: string;
  Key: string;
  IsFeature: boolean;
  AdminNavigation: boolean;
  PortalNavigation: boolean;
  BusinessNavigation: boolean;
}
